import { Plugin, PluginKey } from 'prosemirror-state';
import { Decoration, DecorationSet } from 'prosemirror-view';

export const misspellingPluginKey = new PluginKey('misspelling-highlight');

/**
 * Creates a ProseMirror plugin that highlights misspelled words and handles clicks on them
 */
export function createMisspellingPlugin(
  misspelledWords: string[],
  onWordClick?: (word: string) => void,
  troubleWords?: Array<{ correct_spelling: string; misspellings: string[] }>,
) {
  // Helper function to escape special regex characters
  const escapeRegExp = (string: string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  // Build a list of all words to highlight (misspellings + correct spellings)
  const wordsToHighlight: string[] = [...misspelledWords];
  
  // If we have troubleWords, also include correct spellings that have misspellings
  if (troubleWords) {
    troubleWords.forEach(troubleWord => {
      // Add correct spelling if any of its misspellings are in the text
      if (troubleWord.misspellings.some(m => 
        misspelledWords.some(mw => mw.toLowerCase() === m.toLowerCase())
      )) {
        wordsToHighlight.push(troubleWord.correct_spelling);
      }
    });
  }

  // Remove duplicates and filter empty strings
  const uniqueWords = Array.from(new Set(wordsToHighlight.filter(word => word && word.trim())));
  
  if (uniqueWords.length === 0) {
    // Return a minimal plugin if there are no words to highlight
    return new Plugin({
      key: misspellingPluginKey,
      state: {
        init() { return DecorationSet.empty; },
        apply(tr, old) { return old.map(tr.mapping, tr.doc); }
      },
      props: {
        decorations(state) { return DecorationSet.empty; }
      }
    });
  }

  // Create regex pattern for all words to highlight
  const pattern = uniqueWords
    .map(word => escapeRegExp(word))
    .join('|');
  
  const regex = new RegExp(`\\b(${pattern})\\b`, 'gi');

  // Function to find all matches in the document
  function findMatches(doc: any): Array<{ from: number; to: number; word: string }> {
    const matches: Array<{ from: number; to: number; word: string }> = [];
    
    doc.descendants((node: any, pos: number) => {
      if (node.isText && node.text) {
        let match;
        const text = node.text;
        
        // Reset regex for each text node
        regex.lastIndex = 0;
        
        while ((match = regex.exec(text)) !== null) {
          const from = pos + match.index;
          const to = from + match[0].length;
          matches.push({ from, to, word: match[0] });
        }
      }
    });
    
    return matches;
  }

  // Create decorations for all matches
  function createDecorations(doc: any): DecorationSet {
    const matches = findMatches(doc);
    const decorations = matches.map(match => {
      // Create an inline decoration with styling and data attributes
      return Decoration.inline(match.from, match.to, {
        class: 'misspelling-highlight',
        style: 'background-color: #FFE4E1; cursor: pointer;',
        'data-word': match.word,
        'data-clickable': 'true',
      });
    });
    
    return DecorationSet.create(doc, decorations);
  }

  return new Plugin({
    key: misspellingPluginKey,
    state: {
      init(_, { doc }) {
        return createDecorations(doc);
      },
      apply(tr, old) {
        // If the document changed, recreate decorations
        if (tr.docChanged) {
          return createDecorations(tr.doc);
        }
        // Otherwise, just map the old decorations through the transaction
        return old.map(tr.mapping, tr.doc);
      }
    },
    props: {
      decorations(state) {
        return this.getState(state);
      },
      handleClick(view, pos, event) {
        // Check if the click was on a highlighted word
        const target = event.target as HTMLElement;
        
        // Check if we clicked on an element with our decoration
        if (target && target.closest('[data-clickable="true"]')) {
          const wordElement = target.closest('[data-word]') as HTMLElement;
          if (wordElement) {
            const word = wordElement.getAttribute('data-word');
            if (word && onWordClick) {
              event.preventDefault();
              event.stopPropagation();
              onWordClick(word);
              return true; // Handled the event
            }
          }
        }
        
        // Alternative: Check decorations at click position
        const decorations = misspellingPluginKey.getState(view.state);
        if (decorations) {
          const found = decorations.find(pos, pos);
          if (found.length > 0) {
            // Get the text at this position
            const doc = view.state.doc;
            const $pos = doc.resolve(pos);
            const node = $pos.parent;
            
            if (node.isTextblock) {
              // Find the word at this position
              const text = node.textContent;
              const offset = pos - $pos.before($pos.depth);
              
              // Find word boundaries
              let start = offset;
              let end = offset;
              
              while (start > 0 && /\w/.test(text[start - 1])) start--;
              while (end < text.length && /\w/.test(text[end])) end++;
              
              const word = text.slice(start, end);
              
              if (word && uniqueWords.some(w => w.toLowerCase() === word.toLowerCase())) {
                if (onWordClick) {
                  event.preventDefault();
                  event.stopPropagation();
                  onWordClick(word);
                  return true; // Handled the event
                }
              }
            }
          }
        }
        
        return false; // Let other handlers process this
      }
    }
  });
}

/**
 * Updates an existing misspelling plugin with new words
 */
export function updateMisspellingPlugin(
  view: any,
  misspelledWords: string[],
  onWordClick?: (word: string) => void,
  troubleWords?: Array<{ correct_spelling: string; misspellings: string[] }>,
) {
  const state = view.state;
  const plugins = state.plugins.filter((p: Plugin) => (p as any).key !== misspellingPluginKey);
  const newPlugin = createMisspellingPlugin(misspelledWords, onWordClick, troubleWords);
  
  view.updateState(
    state.reconfigure({
      plugins: [...plugins, newPlugin]
    })
  );
}